import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateClientData,
  CreateClientLocationData,
  CreateClientResourceInputData,
  CreateClientResourceOutputData,
  CreateClientResourceTransferData,
  CreateClientTrayInputData,
  CreateClientTrayOutputData,
  CreateClientTrayTransferData,
  CreateClientVarietyInputData,
  CreateClientVarietyOutputData,
  CreateClientVarietyTransferData,
  GetClientResourceInventorySummaryList,
  GetClientSeedBatchesList,
  GetClientSeedInventorySummaryList,
  GetClientTrayInventorySummaryList,
  UpdateClientData,
  UpdateClientLocationData,
} from 'api/client';
import {
  PaginationData,
  Client,
  ClientLocation,
  ResourceInventorySummary,
  TrayInventorySummary,
  SeedInventorySummary,
  SeedBatch,
} from 'constants/interface';
import { RootState } from 'state';

const clientEntityAdapter = createEntityAdapter<Client>();
const locationEntityAdapter = createEntityAdapter<ClientLocation>();
const batchesEntityAdapter = createEntityAdapter<SeedBatch>();

export interface ClientResourceInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: ResourceInventorySummary[]
}

export interface ClientTrayInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: TrayInventorySummary[]
}

export interface ClientSeedInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: SeedInventorySummary[]
}

export interface LocationsState extends EntityState<ClientLocation> {
  isLoading: boolean
  remoteTotal: number
}

export interface SeedBatchesState extends EntityState<
SeedBatch
> {
  isLoading: boolean
}

export interface ClientsState extends EntityState<Client> {
  isLoading: boolean
  remoteTotal: number
  locations: LocationsState
  resourceInventories: ClientResourceInventoriesState
  trayInventories: ClientTrayInventoriesState
  seedInventories: ClientSeedInventoriesState
  seedBatches: SeedBatchesState
}

export const initialState: ClientsState = clientEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
  locations: locationEntityAdapter.getInitialState({
    isLoading: false,
    remoteTotal: 0,
  }),
  resourceInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  trayInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  seedInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  seedBatches: batchesEntityAdapter.getInitialState({
    isLoading: false,
  }),
});

// Client
export const createClientAction = createAsyncThunk(
  'clients/create',
  async (payload: CreateClientData) => {
    const clientData = await apiClient.client.create(payload);
    return clientData;
  },
);

export const updateClientAction = createAsyncThunk(
  'clients/update',
  async (payload: UpdateClientData) => {
    const clientData = await apiClient.client.update(payload);
    return clientData;
  },
);

export const getClientsListAction = createAsyncThunk(
  'clients/list',
  async (payload: PaginationData) => {
    const response = await apiClient.client.getList(payload);

    return response;
  },
);

// Locations
export const createClientLocationAction = createAsyncThunk(
  'locations/create',
  async (payload: {
    clientId: string
    locationData: CreateClientLocationData
  }) => {
    const clientData = await apiClient
      .client
      .createLocation(payload.clientId, payload.locationData);
    return clientData;
  },
);

export const updateClientLocationAction = createAsyncThunk(
  'locations/update',
  async (payload: UpdateClientLocationData) => {
    const clientData = await apiClient
      .client
      .updateLocation(payload);
    return clientData;
  },
);

export const getClientLocationsListAction = createAsyncThunk(
  'locations/list',
  async (payload: {
    clientId: string
    paginationData: PaginationData
  }) => {
    const response = await apiClient
      .client
      .getLocationsList(payload.clientId, payload.paginationData);

    return response;
  },
);

// Resources
export const createClientResourceInputAction = createAsyncThunk(
  'clients/resources/input',
  async (payload: CreateClientResourceInputData) => {
    const entryData = await apiClient.client.createClientResourceInput(payload);
    return entryData;
  },
);

export const createClientResourceOutputAction = createAsyncThunk(
  'clients/resources/output',
  async (payload: CreateClientResourceOutputData) => {
    const entryData = await apiClient.client.createClientResourceOutput(payload);
    return entryData;
  },
);

export const createClientResourceTransferAction = createAsyncThunk(
  'clients/resources/transfer',
  async (payload: CreateClientResourceTransferData) => {
    const entryData = await apiClient.client.createClientResourceTransfer(payload);
    return entryData;
  },
);

export const getClientResourceInventoriesListAction = createAsyncThunk(
  'clients/resources/list',
  async (payload: GetClientResourceInventorySummaryList) => {
    const response = await apiClient.client.getResourceInventorySummaryList(payload);

    return response;
  },
);

// Trays
export const createClientTrayInputAction = createAsyncThunk(
  'clients/trays/input',
  async (payload: CreateClientTrayInputData) => {
    const entryData = await apiClient.client.createClientTrayInput(payload);
    return entryData;
  },
);

export const createClientTrayOutputAction = createAsyncThunk(
  'clients/trays/output',
  async (payload: CreateClientTrayOutputData) => {
    const entryData = await apiClient.client.createClientTrayOutput(payload);
    return entryData;
  },
);

export const createClientTrayTransferAction = createAsyncThunk(
  'clients/trays/transfer',
  async (payload: CreateClientTrayTransferData) => {
    const entryData = await apiClient.client.createClientTrayTransfer(payload);
    return entryData;
  },
);

export const getClientTrayInventoriesListAction = createAsyncThunk(
  'clients/trays/list',
  async (payload: GetClientTrayInventorySummaryList) => {
    const response = await apiClient.client.getTrayInventorySummaryList(payload);

    return response;
  },
);

// Clients seeds
export const createClientVarietyInputAction = createAsyncThunk(
  'clients/varieties/input',
  async (payload: CreateClientVarietyInputData) => {
    const entryData = await apiClient.client.createClientVarietyInput(payload);
    return entryData;
  },
);

export const createClientVarietyOutputAction = createAsyncThunk(
  'clients/varieties/output',
  async (payload: CreateClientVarietyOutputData) => {
    const entryData = await apiClient.client.createClientVarietyOutput(payload);
    return entryData;
  },
);

export const createClientVarietyTransferAction = createAsyncThunk(
  'clients/varieties/transfer',
  async (payload: CreateClientVarietyTransferData) => {
    const entryData = await apiClient.client.createClientVarietyTransfer(payload);
    return entryData;
  },
);

export const getClientSeedBatchesListAction = createAsyncThunk(
  'clients/varieties/batches',
  async (payload: GetClientSeedBatchesList) => {
    const response = await apiClient.client.getSeedBatchesList(payload);

    return response;
  },
);

export const getClientSeedInventoriesListAction = createAsyncThunk(
  'clients/varieties/list',
  async (payload: GetClientSeedInventorySummaryList) => {
    const response = await apiClient.client.getSeedInventorySummaryList(payload);

    return response;
  },
);

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    cleanClientsAction: clientEntityAdapter.removeAll,
    cleanLocationsAction: (state) => {
      locationEntityAdapter.removeAll(state.locations);
    },
    cleanClientResourcesAction: (state) => {
      state.resourceInventories.items = [];
    },
    cleanClientTraysAction: (state) => {
      state.trayInventories.items = [];
    },
    cleanClientSeedsAction: (state) => {
      state.seedInventories.items = [];
    },
    cleanClientSeedBatchesAction: (state) => {
      batchesEntityAdapter.removeAll(state.seedBatches);
    },
  },
  extraReducers: (builder) => {
    // Clients
    builder.addCase(getClientsListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getClientsListAction.fulfilled, (state, action) => {
      clientEntityAdapter.removeAll(state);
      clientEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getClientsListAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Locations
    builder.addCase(getClientLocationsListAction.pending, (state) => {
      state.locations.isLoading = true;
    });
    builder.addCase(getClientLocationsListAction.fulfilled, (state, action) => {
      locationEntityAdapter.removeAll(state.locations);
      locationEntityAdapter.addMany(state.locations, action.payload.data);
      state.locations.remoteTotal = action.payload.pagination.total;
      state.locations.isLoading = false;
    });
    builder.addCase(getClientLocationsListAction.rejected, (state) => {
      state.locations.isLoading = false;
    });

    // Resources
    builder.addCase(getClientResourceInventoriesListAction.pending, (state) => {
      state.resourceInventories.isLoading = true;
    });
    builder.addCase(getClientResourceInventoriesListAction.fulfilled, (state, action) => {
      state.resourceInventories.items = action.payload.data;
      state.resourceInventories.remoteTotal = action.payload.pagination.total;
      state.resourceInventories.isLoading = false;
    });
    builder.addCase(getClientResourceInventoriesListAction.rejected, (state) => {
      state.resourceInventories.isLoading = false;
    });

    // Trays
    builder.addCase(getClientTrayInventoriesListAction.pending, (state) => {
      state.trayInventories.isLoading = true;
    });
    builder.addCase(getClientTrayInventoriesListAction.fulfilled, (state, action) => {
      state.trayInventories.items = action.payload.data;
      state.trayInventories.remoteTotal = action.payload.pagination.total;
      state.trayInventories.isLoading = false;
    });
    builder.addCase(getClientTrayInventoriesListAction.rejected, (state) => {
      state.trayInventories.isLoading = false;
    });

    // Seeds
    builder.addCase(getClientSeedInventoriesListAction.pending, (state) => {
      state.seedInventories.isLoading = true;
    });
    builder.addCase(getClientSeedInventoriesListAction.fulfilled, (state, action) => {
      state.seedInventories.items = action.payload.data;
      state.seedInventories.remoteTotal = action.payload.pagination.total;
      state.seedInventories.isLoading = false;
    });
    builder.addCase(getClientSeedInventoriesListAction.rejected, (state) => {
      state.seedInventories.isLoading = false;
    });

    builder.addCase(getClientSeedBatchesListAction.pending, (state) => {
      state.seedBatches.isLoading = true;
    });
    builder.addCase(getClientSeedBatchesListAction.fulfilled, (state, action) => {
      batchesEntityAdapter.removeAll(state.seedBatches);
      batchesEntityAdapter.addMany(state.seedBatches, action.payload);
      state.seedBatches.isLoading = false;
    });
    builder.addCase(getClientSeedBatchesListAction.rejected, (state) => {
      state.seedBatches.isLoading = false;
    });
  },
});

export const {
  cleanClientsAction,
  cleanLocationsAction,
  cleanClientResourcesAction,
  cleanClientTraysAction,
  cleanClientSeedsAction,
  cleanClientSeedBatchesAction,
} = clientsSlice.actions;

// Clients

export const isLoadingClientsSelector = (state: RootState):
  boolean => state.clients.isLoading;

export const clientsEntitiesSelector = (state: RootState):
  Dictionary<Client> => state.clients.entities;

export const clientsIdsSelector = (state: RootState):
  EntityId[] => state.clients.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.clients.remoteTotal;

// Locations

export const isLoadingLocationsSelector = (state: RootState):
  boolean => state.clients.locations.isLoading;

export const locationsEntitiesSelector = (state: RootState):
  Dictionary<ClientLocation> => state.clients.locations.entities;

export const locationsIdsSelector = (state: RootState):
  EntityId[] => state.clients.locations.ids;

export const locationRemoteTotalSelector = (state: RootState):
  number => state.clients.locations.remoteTotal;

// Resources

export const isLoadingClientResourcesSelector = (state: RootState):
boolean => state.clients.resourceInventories.isLoading;

export const clientResourcesItemsSelector = (state: RootState):
ResourceInventorySummary[] => state.clients.resourceInventories.items;

export const clientResourcesRemoteTotalSelector = (state: RootState):
number => state.clients.resourceInventories.remoteTotal;

// Trays

export const isLoadingClientTraysSelector = (state: RootState):
  boolean => state.clients.trayInventories.isLoading;

export const clientTraysItemsSelector = (state: RootState):
  TrayInventorySummary[] => state.clients.trayInventories.items;

export const clientTraysRemoteTotalSelector = (state: RootState):
  number => state.clients.trayInventories.remoteTotal;

// Seeds

export const isLoadingClientSeedsSelector = (state: RootState):
  boolean => state.clients.seedInventories.isLoading;

export const clientSeedsItemsSelector = (state: RootState):
  SeedInventorySummary[] => state.clients.seedInventories.items;

export const clientSeedsRemoteTotalSelector = (state: RootState):
  number => state.clients.seedInventories.remoteTotal;

export const isLoadingClientSeedBatchesSelector = (state: RootState):
  boolean => state.clients.seedBatches.isLoading;

export const clientSeedBatchesEntitiesSelector = (state: RootState):
  Dictionary<SeedBatch> => state.clients.seedBatches.entities;

export const clientSeedBatchesIdsSelector = (state: RootState):
  EntityId[] => state.clients.seedBatches.ids;
